// src/views/fechamentos/FechamentosList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../services/api";

const fmtComp = c => {
  if (!c) return "";
  const [y, m] = c.split("-");
  const meses = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  return `${meses[parseInt(m) - 1]}/${y}`;
};
const fmtM  = v => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDT = d => { try { return new Date(d).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return d || "—"; } };

// ── Banner ──────────────────────────────────────────────────────────────
function Banner({ tipo = "info", children, onClose }) {
  const cores = {
    error:   { bg: "#FEE2E2", border: "#FECACA", color: "#991B1B", icon: "bi-exclamation-octagon" },
    success: { bg: "#DCFCE7", border: "#BBF7D0", color: "#166534", icon: "bi-check-circle" },
    info:    { bg: "#EFF6FF", border: "#BFDBFE", color: "#1D4ED8", icon: "bi-info-circle" },
    warning: { bg: "#FEF3C7", border: "#FDE68A", color: "#92400E", icon: "bi-exclamation-triangle" },
  };
  const c = cores[tipo] || cores.info;
  if (!children) return null;
  return (
    <div style={{
      background: c.bg, border: `1px solid ${c.border}`, color: c.color,
      borderRadius: 8, padding: "10px 14px", marginBottom: 12,
      display: "flex", alignItems: "center", gap: 10, fontSize: 13.5,
    }}>
      <i className={`bi ${c.icon}`} style={{ fontSize: 18 }} />
      <div style={{ flex: 1 }}>{children}</div>
      {onClose && (
        <button className="btn btn-sm" style={{ padding: "2px 8px", background: "transparent", color: c.color }} onClick={onClose}>
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
}

export default function FechamentosList() {
  const { condominioAtivo, isAdmin, podeEditar } = useAuth();
  const canFechar  = podeEditar("fechamentoMensal") || podeEditar("fechamento-mensal");

  const [idCondo,    setIdCondo]    = useState("");
  const [dados,      setDados]      = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [compSel,    setCompSel]    = useState("");
  const [pendencias, setPendencias] = useState(null);
  const [loadPend,   setLoadPend]   = useState(false);
  const [obs,        setObs]        = useState("");
  const [dtFechamento, setDtFechamento] = useState(new Date().toISOString().split("T")[0]);
  const [saving,     setSaving]     = useState(false);

  const [page,       setPage]       = useState(1);
  const [limit]                     = useState(24);

  const [pageError,  setPageError]  = useState("");
  const [pageSuccess, setPageSuccess] = useState("");

  // Modais
  const [showFechar,   setShowFechar]   = useState(false);
  const [showReabrir,  setShowReabrir]  = useState(null); // {id, competencia}
  const [motivoReab,   setMotivoReab]   = useState("");
  const [showNotificar, setShowNotificar] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (condominioAtivo?.id) setIdCondo(String(condominioAtivo.id));
    else setIdCondo("");
  }, [condominioAtivo]);

  useEffect(() => {
    if (!pageSuccess) return;
    const t = setTimeout(() => setPageSuccess(""), 5000);
    return () => clearTimeout(t);
  }, [pageSuccess]);

  const carregar = useCallback(async () => {
    if (!idCondo) return;
    setLoading(true);
    try {
      const r = await api("GET", `/fechamentos?idCondominio=${idCondo}&page=${page}&limit=${limit}`);
      if (r.success) setDados(r);
    } catch (e) {
      setPageError(e?.message || "Falha ao carregar fechamentos.");
    } finally { setLoading(false); }
  }, [idCondo, page, limit]);

  useEffect(() => { carregar(); }, [carregar]);

  const verificarPendencias = async (comp) => {
    if (!idCondo) { setPageError("Selecione um condomínio."); return; }
    setCompSel(comp); setPendencias(null); setLoadPend(true);
    try {
      const r = await api("GET", `/fechamentos/pendencias?idCondominio=${idCondo}&competencia=${comp}`);
      if (r.success) setPendencias(r);
      else setPendencias({ error: r.message || "Erro ao verificar pendências." });
    } catch (e) {
      setPendencias({ error: e?.message || "Erro ao verificar pendências." });
    } finally { setLoadPend(false); }
  };

  const handleAbrirFechar = () => {
    if (!compSel || !dtFechamento) {
      setPageError("Selecione uma competência e a data de fechamento.");
      return;
    }
    setShowFechar(true);
  };

  const handleConfirmarFechar = async () => {
    setActionLoading(true); setSaving(true);
    try {
      const r = await api("POST", "/fechamentos", {
        idCondominio: idCondo,
        competencia: compSel,
        observacao: obs,
        dtFechamento,
      });
      if (r.success) {
        setPageSuccess(r.message);
        setCompSel(""); setPendencias(null); setObs("");
        setDtFechamento(new Date().toISOString().split("T")[0]);
        setShowFechar(false);
        carregar();
      } else {
        setPageError(r.message || "Falha ao fechar.");
      }
    } catch (e) {
      setPageError(e?.message || "Falha ao fechar competência.");
    } finally {
      setSaving(false); setActionLoading(false);
    }
  };

  const handleVerDemonstrativo = async (id) => {
    try {
      const r = await api("GET", `/fechamentos/${id}/demonstrativo`);
      if (r.html) {
        const blob = new Blob([r.html], { type: "text/html;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank", "width=950,height=900");
        if (w) { w.onload = () => setTimeout(() => w.print(), 500); }
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      } else {
        setPageError("Demonstrativo não disponível para este fechamento.");
      }
    } catch (e) {
      setPageError(e?.message || "Falha ao abrir demonstrativo.");
    }
  };

  const handlePublicar = async (id, publicado) => {
    try {
      const r = await api("PATCH", `/fechamentos/${id}/publicar`, { publicado: !publicado });
      setPageSuccess(r.message);
      carregar();
    } catch (e) {
      setPageError(e?.message || "Falha ao publicar/despublicar.");
    }
  };

  const handleConfirmarNotificar = async () => {
    if (!showNotificar) return;
    setActionLoading(true);
    try {
      const r = await api("POST", `/fechamentos/${showNotificar.id}/notificar`, {});
      if (r.success) setPageSuccess(r.message);
      else setPageError(r.message || "Falha ao notificar.");
      setShowNotificar(null);
    } catch (e) {
      setPageError(e?.message || "Falha ao notificar.");
    } finally { setActionLoading(false); }
  };

  const handleConfirmarReabrir = async () => {
    if (!showReabrir) return;
    setActionLoading(true);
    try {
      const r = await api("PATCH", `/fechamentos/${showReabrir.id}/reabrir`, { motivo: motivoReab.trim() });
      if (r.success) {
        setPageSuccess(`Competência ${fmtComp(showReabrir.competencia)} reaberta.`);
        setShowReabrir(null); setMotivoReab("");
        carregar();
      } else {
        setPageError(r.message || "Falha ao reabrir.");
      }
    } catch (e) {
      setPageError(e?.message || "Falha ao reabrir.");
    } finally { setActionLoading(false); }
  };

  const bloqueante = dados?.politica === "Bloqueante";
  const podeFechar = compSel && pendencias && !pendencias.error && (!bloqueante || !pendencias.temPendencias) && canFechar;
  const fechamentos = dados?.data || [];

  return (
    <div className="page-container">
      <Banner tipo="error"   onClose={() => setPageError("")}>{pageError}</Banner>
      <Banner tipo="success" onClose={() => setPageSuccess("")}>{pageSuccess}</Banner>

      <div className="page-header">
        <div>
          <h1 className="page-title"><i className="bi bi-calendar-check me-2" />Fechamento Mensal</h1>
          <p className="page-subtitle">Controle de competências e publicação para o portal do condômino</p>
        </div>
      </div>

      {!idCondo && (
        <Banner tipo="warning">Selecione um condomínio na barra superior para acessar os fechamentos.</Banner>
      )}

      {dados && (
        <div style={{
          background: bloqueante ? "#FEF3C7" : "#EFF6FF",
          border: `1px solid ${bloqueante ? "#FCD34D" : "#BFDBFE"}`,
          borderRadius: 8, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10,
          marginBottom: 16,
        }}>
          <i className={`bi ${bloqueante ? "bi-shield-lock-fill" : "bi-info-circle-fill"}`}
            style={{ color: bloqueante ? "#D97706" : "#2563EB", fontSize: 18 }} />
          <div>
            <strong>Política: {dados.politica}</strong>
            {bloqueante
              ? " — Fechamento só é permitido após quitar todas as pendências do mês."
              : " — Fechamento é permitido mesmo com pendências (apenas informativo)."}
          </div>
        </div>
      )}

      <div className="row g-4">
        {/* Fechar competência */}
        <div className="col-md-5">
          <div className="card">
            <div className="card-header"><h5 className="mb-0"><i className="bi bi-lock me-2" />Fechar Competência</h5></div>
            <div className="card-body">
              {loading ? <div className="text-center py-3"><span className="spinner-border text-primary" /></div>
                : !canFechar ? <div className="alert alert-info py-2 mb-0" style={{fontSize:12.5}}>
                    <i className="bi bi-info-circle me-1"/>Você não tem permissão para fechar competências.
                  </div>
                : !dados?.mesesDisponiveis?.length ? <div className="text-center text-muted py-4">
                    <i className="bi bi-check-all" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                    <div>Todos os meses estão fechados</div>
                  </div>
                : (<>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Selecione o mês para fechar</label>
                    <div className="d-flex flex-wrap gap-2">
                      {dados.mesesDisponiveis.map(m => (
                        <button key={m} type="button"
                          className={`btn btn-sm ${compSel === m ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => verificarPendencias(m)}>
                          {fmtComp(m)}
                        </button>
                      ))}
                    </div>
                  </div>

                  {loadPend && <div className="text-center py-2"><span className="spinner-border spinner-border-sm text-primary me-2" />Verificando pendências...</div>}

                  {pendencias?.error && (
                    <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 12 }}>
                      <i className="bi bi-exclamation-triangle me-1" />{pendencias.error}
                    </div>
                  )}
                  {pendencias && !pendencias.error && compSel && (
                    <div className="mb-3">
                      <div className="fw-semibold mb-2">Pendências em {fmtComp(compSel)}:</div>
                      {[
                        { label: "Contas a Pagar em aberto", ...pendencias.pendencias.contasPagar,   icon: "bi-receipt" },
                        { label: "Boletos sem pagamento",    ...pendencias.pendencias.boletos,      icon: "bi-file-earmark-text" },
                        { label: "Contas a Receber em aberto", ...pendencias.pendencias.contasReceber, icon: "bi-arrow-down-circle" },
                      ].map(p => (
                        <div key={p.label} style={{
                          display: "flex", justifyContent: "space-between", alignItems: "center",
                          padding: "6px 10px", borderRadius: 6, marginBottom: 4,
                          background: p.qtd > 0 ? "#FEF2F2" : "#F0FDF4",
                          border: `1px solid ${p.qtd > 0 ? "#FECACA" : "#BBF7D0"}`,
                        }}>
                          <span style={{ fontSize: 13 }}><i className={`bi ${p.icon} me-2`} />{p.label}</span>
                          <span style={{ fontWeight: 700, color: p.qtd > 0 ? "#DC2626" : "#16A34A", fontSize: 13 }}>
                            {p.qtd > 0 ? `${p.qtd} item(s) — ${fmtM(p.total)}` : "OK"}
                          </span>
                        </div>
                      ))}

                      {!pendencias.error && bloqueante && pendencias.temPendencias && (
                        <div className="alert alert-warning mt-2 mb-0 py-2" style={{ fontSize: 12 }}>
                          <i className="bi bi-exclamation-triangle me-1" />
                          Política <strong>Bloqueante</strong>: resolva as pendências antes de fechar.
                        </div>
                      )}
                      {!pendencias.error && !pendencias.temPendencias && (
                        <div className="alert alert-success mt-2 mb-0 py-2" style={{ fontSize: 12 }}>
                          <i className="bi bi-check-circle me-1" />Nenhuma pendência. Pronto para fechar!
                        </div>
                      )}
                    </div>
                  )}

                  {compSel && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">Data de Fechamento <span className="required-mark">*</span></label>
                        <input type="date" className="form-control" value={dtFechamento}
                          onChange={e => setDtFechamento(e.target.value)} />
                        <small className="text-muted">Lançamentos com data anterior a esta serão bloqueados após o fechamento.</small>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Observação (opcional)</label>
                        <textarea className="form-control" rows={2} value={obs}
                          onChange={e => setObs(e.target.value)}
                          placeholder="Ex: Revisado pelo síndico em reunião de 01/05" />
                      </div>
                    </>
                  )}

                  <button className="btn btn-primary w-100" disabled={!podeFechar || saving} onClick={handleAbrirFechar}>
                    <i className="bi bi-lock-fill me-2" />Fechar {compSel ? fmtComp(compSel) : ""}
                  </button>
                </>)}
            </div>
          </div>
        </div>

        {/* Histórico de fechamentos */}
        <div className="col-md-7">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0"><i className="bi bi-clock-history me-2" />Histórico de Fechamentos</h5>
              {dados?.pagination?.totalPages > 1 && (
                <div className="d-flex gap-2 align-items-center" style={{ fontSize: 12 }}>
                  <button className="btn btn-outline-secondary btn-sm" disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}>
                    <i className="bi bi-chevron-left" />
                  </button>
                  <span>{page}/{dados.pagination.totalPages}</span>
                  <button className="btn btn-outline-secondary btn-sm" disabled={page >= dados.pagination.totalPages}
                    onClick={() => setPage(p => p + 1)}>
                    <i className="bi bi-chevron-right" />
                  </button>
                </div>
              )}
            </div>
            <div className="card-body p-0">
              {!fechamentos.length
                ? <div className="text-center text-muted py-5">
                    <i className="bi bi-calendar-x" style={{ fontSize: 36, display: "block", marginBottom: 8 }} />
                    <div>Nenhum fechamento realizado</div>
                  </div>
                : <table className="table table-hover mb-0" style={{ fontSize: 13 }}>
                    <thead><tr style={{ background:"var(--bg-surface-alt)" }}>
                      <th className="ps-3">Competência</th>
                      <th>Fechado em</th>
                      <th>Por</th>
                      <th>Status</th>
                      <th className="text-center">Portal</th>
                      <th className="text-center" style={{ width: 140 }}>Ações</th>
                    </tr></thead>
                    <tbody>
                      {fechamentos.map(f => (
                        <tr key={f.id}>
                          <td className="ps-3 fw-semibold">{fmtComp(f.competencia)}</td>
                          <td style={{ color:"var(--gray-500)" }}>{fmtDT(f.dtFechamento)}</td>
                          <td style={{ color:"var(--gray-500)" }}>{f.fechadoPor}</td>
                          <td>
                            <span style={{
                              padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700,
                              background: f.status === "Fechado" ? "#D1FAE5" : "#FEE2E2",
                              color:      f.status === "Fechado" ? "#065F46" : "#991B1B",
                            }}>
                              {f.status === "Fechado" ? "Fechado" : "Reaberto"}
                            </span>
                          </td>
                          <td className="text-center">
                            {f.status === "Fechado" && canFechar && (
                              <button
                                className={`btn btn-sm ${f.publicadoPortal ? "btn-success" : "btn-outline-secondary"}`}
                                onClick={() => handlePublicar(f.id, f.publicadoPortal)}
                                title={f.publicadoPortal ? "Publicado — clique para despublicar" : "Publicar no portal"}>
                                <i className={`bi bi-${f.publicadoPortal ? "eye-fill" : "eye-slash"}`} />
                              </button>
                            )}
                          </td>
                          <td className="text-center">
                            <div className="table-actions">
                              {f.status === "Fechado" && f.temDemonstrativo && (
                                <button className="btn btn-outline-primary btn-sm btn-icon"
                                  onClick={() => handleVerDemonstrativo(f.id)}
                                  title="Ver/Imprimir Demonstrativo">
                                  <i className="bi bi-file-earmark-pdf" />
                                </button>
                              )}
                              {f.status === "Fechado" && f.publicadoPortal && canFechar && (
                                <button className="btn btn-outline-info btn-sm btn-icon"
                                  onClick={() => setShowNotificar(f)}
                                  title="Notificar condôminos por email">
                                  <i className="bi bi-envelope" />
                                </button>
                              )}
                              {f.status === "Fechado" && isAdmin && (
                                <button className="btn btn-outline-warning btn-sm btn-icon"
                                  onClick={() => { setMotivoReab(""); setShowReabrir(f); }}
                                  title="Reabrir competência (Admin)">
                                  <i className="bi bi-unlock" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          </div>
        </div>
      </div>

      {/* ── MODAL: Confirmar fechamento ── */}
      {showFechar && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-lock text-primary me-2" />Confirmar Fechamento</h5>
                <button type="button" className="btn-close" onClick={() => !actionLoading && setShowFechar(false)} />
              </div>
              <div className="modal-body">
                <Banner tipo="warning">
                  Após o fechamento, lançamentos com data anterior a <strong>{new Date(dtFechamento + "T12:00:00").toLocaleDateString("pt-BR")}</strong> ficarão bloqueados.
                  O demonstrativo será gerado e publicado no portal.
                </Banner>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 13.5 }}>
                  <div><strong>Competência:</strong> {fmtComp(compSel)}</div>
                  <div><strong>Data de fechamento:</strong> {new Date(dtFechamento + "T12:00:00").toLocaleDateString("pt-BR")}</div>
                </div>
                {obs && <div style={{ marginTop: 10, fontSize: 13 }}><strong>Observação:</strong> {obs}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowFechar(false)} disabled={actionLoading}>Voltar</button>
                <button className="btn btn-primary" onClick={handleConfirmarFechar} disabled={actionLoading}>
                  {actionLoading
                    ? <><span className="spinner-border spinner-border-sm me-2" />Fechando...</>
                    : <><i className="bi bi-lock-fill me-2" />Confirmar Fechamento</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Reabrir ── */}
      {showReabrir && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-unlock text-warning me-2" />Reabrir competência {fmtComp(showReabrir.competencia)}</h5>
                <button type="button" className="btn-close" onClick={() => !actionLoading && setShowReabrir(null)} />
              </div>
              <div className="modal-body">
                <Banner tipo="warning">
                  Reabrir permite lançamentos retroativos neste período.
                  <strong> Use somente quando estritamente necessário.</strong>
                </Banner>
                <label className="form-label">Motivo (recomendado)</label>
                <textarea className="form-control" rows={3} value={motivoReab}
                  onChange={e => setMotivoReab(e.target.value)}
                  placeholder="Ex.: lançamento de despesa retroativa autorizado pelo síndico."
                  autoFocus />
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowReabrir(null)} disabled={actionLoading}>Voltar</button>
                <button className="btn btn-warning" onClick={handleConfirmarReabrir} disabled={actionLoading}>
                  {actionLoading
                    ? <><span className="spinner-border spinner-border-sm me-2" />Reabrindo...</>
                    : <><i className="bi bi-unlock me-2" />Confirmar Reabertura</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: Notificar ── */}
      {showNotificar && (
        <div className="modal d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,.4)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title"><i className="bi bi-envelope text-primary me-2" />Notificar Condôminos</h5>
                <button type="button" className="btn-close" onClick={() => !actionLoading && setShowNotificar(null)} />
              </div>
              <div className="modal-body">
                <p style={{ fontSize: 14 }}>
                  Enviar e-mail para todos os condôminos com o demonstrativo da competência{" "}
                  <strong>{fmtComp(showNotificar.competencia)}</strong>?
                </p>
                <small className="text-muted">
                  Só serão notificados os condôminos com e-mail cadastrado e cuja unidade está ativa.
                </small>
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary" onClick={() => setShowNotificar(null)} disabled={actionLoading}>Voltar</button>
                <button className="btn btn-primary" onClick={handleConfirmarNotificar} disabled={actionLoading}>
                  {actionLoading
                    ? <><span className="spinner-border spinner-border-sm me-2" />Enviando...</>
                    : <><i className="bi bi-send me-2" />Enviar</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
